import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { useRunWhenFormsClean } from "@shared";

import { SrvProceedingListItemResponse } from "@/api";
import { NEW_SRV_PROCEEDING_ID, SRV_PROCEEDING_ID_PARAM } from "@/router/ClientRoutes";

/**
 * The opened Verfahren, kept in the URL like the opened Aktenvermerk: shareable, refresh-safe, and every
 * switch — another Verfahren, a new draft, back — is a navigation the unsaved-changes guard covers.
 */
export function useSrvProceedingSelection(proceedings: SrvProceedingListItemResponse[] | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const runWhenFormsClean = useRunWhenFormsClean();
  const previousSelection = useRef<string | null>(null);

  const param = searchParams.get(SRV_PROCEEDING_ID_PARAM);
  const isCreating = param === NEW_SRV_PROCEEDING_ID;
  const selectedProceeding = isCreating ? null : (proceedings?.find((p) => p.id === param) ?? proceedings?.[0] ?? null);

  const setParam = useCallback(
    (proceedingId: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          if (proceedingId) {
            params.set(SRV_PROCEEDING_ID_PARAM, proceedingId);
          } else {
            params.delete(SRV_PROCEEDING_ID_PARAM);
          }

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Point the URL at what is actually shown: on first load, and when the linked Verfahren no longer
  // exists, the newest one stands in.
  useEffect(() => {
    if (!proceedings || isCreating) {
      return;
    }

    const effectiveId = selectedProceeding?.id ?? null;
    if (effectiveId !== param) {
      setParam(effectiveId);
    }
  }, [proceedings, isCreating, selectedProceeding, param, setParam]);

  const startCreate = useCallback(() => {
    previousSelection.current = selectedProceeding?.id ?? null;
    setParam(NEW_SRV_PROCEEDING_ID);
  }, [selectedProceeding, setParam]);

  const cancelCreate = useCallback(
    () => runWhenFormsClean(() => setParam(previousSelection.current)),
    [runWhenFormsClean, setParam]
  );

  const selectCreated = useCallback(
    (proceedingId: string) => runWhenFormsClean(() => setParam(proceedingId)),
    [runWhenFormsClean, setParam]
  );

  return { isCreating, selectedProceeding, select: setParam, startCreate, cancelCreate, selectCreated };
}
